import { HelpCircle } from 'lucide-react'
import { Tooltip } from '../../../components/ui/Tooltip'
import { useScopeStore } from '../store'
import { useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { useUpsellContext } from '../hooks/useUpsellContext'
import { IDV_VENDORS } from '../types'
import { getFieldById, getGroupById, getSectionById } from '../schema'

type TriState = 'yes' | 'no' | 'unknown'

function TriStateToggle({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string
  value: TriState
  onChange: (next: TriState) => void
  disabled: boolean
}) {
  const { mapLabel } = useUpsellContext()
  const options: Array<{ value: TriState; label: string }> = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: mapLabel("Don't know") },
  ]

  return (
    <div className="inline-flex gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer transition-colors ${
            value === opt.value
              ? 'border-primary bg-primary/10 text-[var(--text-primary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
          } ${disabled ? (value === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            className="sr-only"
          />
          {opt.label}
        </label>
      ))}
    </div>
  )
}

const FRONTEND_OPTIONS = [
  { id: 'hosted_onesdk', label: 'HostedOne SDK' },
  { id: 'embedded_onesdk', label: 'Embedded OneSDK' },
  { id: 'none', label: 'None' },
  { id: 'other', label: 'Other' },
  { id: 'unknown', label: "Don't know" },
]

export function BiometricsTab() {
  const { scope, updateBiometricsConfig, addBiometricsFlow, applyBiometricsFlowTemplate, updateBiometricsFlow, removeBiometricsFlow } = useScopeStore()
  const { biometricsConfig } = scope
  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported

  const tabHeaderGroup = getGroupById('biometrics_tab_header')
  const enablementSection = getSectionById('biometrics_enablement')
  const enablementGroup = getGroupById('biometrics_enablement_group')
  const enabledField = getFieldById('biometrics_enabled')

  const vendorSection = getSectionById('biometrics_vendor')
  const vendorGroup = getGroupById('biometrics_vendor_group')
  const vendorField = getFieldById('biometrics_vendor_primary')
  const otherVendorField = getFieldById('biometrics_vendor_other')

  const flowsSection = getSectionById('biometrics_flows')
  const flowsGroup = getGroupById('biometrics_flows_group')

  const frontendSection = getSectionById('biometrics_frontend')
  const frontendGroup = getGroupById('biometrics_frontend_group')
  const frontendChoiceField = getFieldById('biometrics_frontend_choice')
  const frontendOtherField = getFieldById('biometrics_frontend_other')

  const smsSection = getSectionById('biometrics_sms')
  const smsGroup = getGroupById('biometrics_sms_group')
  const smsValidityGroup = getGroupById('biometrics_sms_validity_group')
  const smsTemplateField = getFieldById('biometrics_sms_template')
  const smsValidityHoursField = getFieldById('biometrics_sms_link_validity_hours')


  const brandingSection = getSectionById('biometrics_branding')
  const brandingGroup = getGroupById('biometrics_branding_group')
  const brandingNotesGroup = getGroupById('biometrics_branding_notes_group')
  const rebrandingHostedOneSdkField = getFieldById('biometrics_rebranding_hosted_onesdk')
  const rebrandingDaonField = getFieldById('biometrics_rebranding_daon')
  const extendedBrandingField = getFieldById('biometrics_extended_branding')
  const customRedirectPageField = getFieldById('biometrics_custom_redirect_page')
  const customConsentsField = getFieldById('biometrics_custom_consents')
  const brandingNotesField = getFieldById('biometrics_branding_notes')

  const flowConsentScreenField = getFieldById('biometrics_flow_consent_screen')
  const flowIdFormField = getFieldById('biometrics_flow_id_form')
  const flowIdVerificationField = getFieldById('biometrics_flow_id_verification')
  const flowBiometricsField = getFieldById('biometrics_flow_biometrics')
  const flowReviewScreenField = getFieldById('biometrics_flow_review_screen')
  const flowRedirectPageField = getFieldById('biometrics_flow_redirect_page')
  const flowNameField = getFieldById('biometrics_flow_name')
  const flowIdField = getFieldById('biometrics_flow_id')
  const flowVendorField = getFieldById('biometrics_flow_vendor')

  const enabledValue = biometricsConfig.enabled
  const isEnabled = enabledValue === 'yes'

  // Style for incomplete required fields — suppressed for upsells
  const { incompleteClass, isUpsell, mapLabel } = useUpsellContext()

  const isEnabledIncomplete = !isUpsell && enabledValue === 'unknown'
  const isVendorIncomplete = !isUpsell && isEnabled && (biometricsConfig.vendor === '' || biometricsConfig.vendor === 'unknown')
  const isFrontendIncomplete = !isUpsell && isEnabled && (biometricsConfig.frontend === 'none' || biometricsConfig.frontend === '' || biometricsConfig.frontend === 'unknown')
  const hasValidFlow = biometricsConfig.flows.some(
    (f) =>
      f.idVerificationEnabled === 'yes' ||
      f.idFormEnabled === 'yes' ||
      f.biometricsEnabled === 'yes' ||
      f.reviewScreenEnabled === 'yes' ||
      f.customScreenEnabled === 'yes'
  )
  const hasFlowDocumentTypes = biometricsConfig.flows.some(
    (f) => f.documentTypes.length > 0 && !f.documentTypes.every((d) => d === 'unknown')
  )
  const isFlowsIncomplete = !isUpsell && isEnabled && (!hasValidFlow || !hasFlowDocumentTypes || biometricsConfig.flows.length === 0)

  const toggleFlowDocumentType = (
    flowIndex: number,
    docId: 'passport' | 'drivers_license' | 'medicare_manual' | 'custom' | 'unknown'
  ) => {
    const flow = biometricsConfig.flows[flowIndex]
    if (!flow) return

    const current = Array.isArray(flow.documentTypes) ? flow.documentTypes : []

    if (docId === 'unknown') {
      const next = current.includes('unknown') ? current.filter((d) => d !== 'unknown') : ['unknown']
      updateBiometricsFlow(flowIndex, { documentTypes: next as any })
      return
    }

    const withoutUnknown = current.filter((d) => d !== 'unknown')
    const next = withoutUnknown.includes(docId)
      ? withoutUnknown.filter((d) => d !== docId)
      : [...withoutUnknown, docId]
    updateBiometricsFlow(flowIndex, { documentTypes: next as any })
  }

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium text-[var(--text-primary)]">
            {tabHeaderGroup?.formTitle || 'Biometrics & IDV'}
          </h3>
          <Tooltip
            content={
              tabHeaderGroup?.tooltip ||
              "Configure document scanning and facial verification to verify your customers' identities."
            }
          >
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {tabHeaderGroup?.formDescription ||
            'Biometrics & IDV combines document verification with facial similarity checks to confirm that the person onboarding is the legitimate document holder. This adds a critical layer of fraud prevention to your verification process.'}
        </p>
      </div>

      {/* Enable Biometrics */}
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">
            {enablementSection?.formTitle || enablementGroup?.formTitle || 'Enablement'}
          </h4>
          <Tooltip
            content={
              enablementSection?.formDescription ||
              enablementGroup?.tooltip ||
              'Does this experience need document and/or facial verification capabilities?'
            }
          >
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isEnabledIncomplete ? incompleteClass : ''}`}>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors">
            <input
              type="radio"
              name="bioEnabled"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={enabledValue === 'yes'}
              onChange={() => updateBiometricsConfig('enabled', 'yes')}
              disabled={disabled}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {enabledField?.options?.find((o) => o.value === 'yes')?.label || 'Yes'}
              </span>
              <p className="text-xs text-[var(--text-muted)]">
                Client needs document verification and/or facial similarity.
              </p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors">
            <input
              type="radio"
              name="bioEnabled"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={enabledValue === 'no'}
              onChange={() => updateBiometricsConfig('enabled', 'no')}
              disabled={disabled}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {enabledField?.options?.find((o) => o.value === 'no')?.label || 'No (Not needed)'}
              </span>
              <p className="text-xs text-[var(--text-muted)]">Client has explicitly opted out / not required.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors">
            <input
              type="radio"
              name="bioEnabled"
              className="mt-1 w-4 h-4 text-primary focus:ring-primary"
              checked={enabledValue === 'unknown'}
              onChange={() => updateBiometricsConfig('enabled', 'unknown')}
              disabled={disabled}
            />
            <div>
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {mapLabel(enabledField?.options?.find((o) => o.value === 'unknown')?.label || "Don't know")}
              </span>
              <p className="text-xs text-[var(--text-muted)]">Not confirmed yet.</p>
            </div>
          </label>
        </div>
      </div>
      </div>

      {/* Only show these sections if biometrics is enabled */}
      {isEnabled && (
        <>
          {/* Vendor Selection */}
          <div className="pt-2">
            <div className="flex items-center gap-2">
              <h4 className="text-base font-semibold text-[var(--text-primary)]">
                {vendorSection?.formTitle || vendorGroup?.formTitle || 'Vendor'}
              </h4>
              <Tooltip
                content={
                  vendorGroup?.tooltip ||
                  'Select the identity verification provider that will process document and biometric checks.'
                }
              >
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            </div>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isVendorIncomplete ? incompleteClass : ''}`}>
            <p className="text-xs text-[var(--text-muted)] mt-1 mb-3">
              {vendorSection?.formDescription ||
                'The IDV vendor performs document authenticity checks, OCR extraction, liveness detection, and face comparison. FrankieOne integrates with multiple vendors to provide flexibility without vendor lock-in.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  {vendorField?.formLabel || 'Primary Vendor'}
                </label>
                <select
                  className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  value={biometricsConfig.vendor}
                  onChange={(e) => updateBiometricsConfig('vendor', e.target.value)}
                  disabled={disabled}
                >
                  {(
                    vendorField?.options ||
                    [{ value: 'unknown', label: mapLabel("Don't know") }, { value: '', label: '—' }, ...IDV_VENDORS.map((v) => ({ value: v.id, label: v.label }))]
                  ).map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  <span className="text-primary">*</span>{' '}
                  {vendorGroup?.helperText || 'Required for completion.'}
                </p>
              </div>
              {biometricsConfig.vendor === 'other' && (
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    {otherVendorField?.formLabel || 'Other Vendor Name'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={otherVendorField?.placeholder || 'Enter vendor name'}
                    value={biometricsConfig.otherVendor}
                    onChange={(e) => updateBiometricsConfig('otherVendor', e.target.value)}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
          </div>
          </div>

          {/* Flows */}
          <div className="pt-2">
            <div className="flex items-center gap-2">
              <h4 className="text-base font-semibold text-[var(--text-primary)]">
                {flowsSection?.formTitle || flowsGroup?.formTitle || 'Flows'}
              </h4>
              <Tooltip
                content={
                  flowsGroup?.tooltip ||
                  'Create verification journeys with different screens and settings for different customer types.'
                }
              >
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            </div>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
          <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isFlowsIncomplete ? incompleteClass : ''}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="h-5" />
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-1">Flows are complete verification experiences you send to customers. Each flow consists of screens shown in sequence. You can create up to 5 different flows and choose which one to send to each customer via API based on their risk profile or customer segment.</p>
              </div>
              <div className="flex sm:justify-end">
                <button
                  type="button"
                  className="px-4 py-2 text-sm rounded-lg bg-primary text-white disabled:opacity-50 whitespace-nowrap"
                  onClick={() => addBiometricsFlow()}
                  disabled={disabled || biometricsConfig.flows.length >= 5}
                >
                  Add Flow
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {biometricsConfig.flows.map((flow, idx) => (
                <div key={`${flow.id}-${idx}`} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
                  <div className="p-4 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] relative">
                    <div className="flex flex-col md:flex-row gap-3 items-end">
                      <div className="flex-1 min-w-0">
                        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                          {flowNameField?.formLabel || 'Flow Name'}
                          <Tooltip content={flowNameField?.tooltip || ''}>
                            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                          </Tooltip>
                        </label>
                        <input
                          type="text"
                          className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          placeholder={flowNameField?.placeholder || 'e.g. Standard IDV Flow'}
                          value={flow.name}
                          onChange={(e) => updateBiometricsFlow(idx, { name: e.target.value })}
                          disabled={disabled}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                          {flowIdField?.formLabel || 'Flow ID'}
                          <Tooltip content="Type a new flow ID or pick a template (idv_daon, idv_incode) to pre-fill this flow with the template's defaults.">
                            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                          </Tooltip>
                        </label>
                        <input
                          type="text"
                          list={`flow-id-templates-${idx}`}
                          className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          placeholder={flowIdField?.placeholder || 'e.g. idv_daon, idv_incode, high_risk_flow'}
                          value={flow.id}
                          onChange={(e) => {
                            const newId = e.target.value
                            if ((newId === 'idv_daon' || newId === 'idv_incode') && flow.id !== newId) {
                              applyBiometricsFlowTemplate(idx, newId)
                            } else {
                              updateBiometricsFlow(idx, { id: newId })
                            }
                          }}
                          disabled={disabled}
                        />
                        <datalist id={`flow-id-templates-${idx}`}>
                          <option value="idv_daon">IDV Daon (template — Passport + Driver's Licence, Biometrics)</option>
                          <option value="idv_incode">IDV Incode (template — Passport + Driver's Licence, Biometrics)</option>
                        </datalist>
                      </div>
                      <div className="w-full md:w-40 flex-shrink-0">
                        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                          {flowVendorField?.formLabel || 'Vendor'}
                          <Tooltip content={flowVendorField?.tooltip || ''}>
                            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                          </Tooltip>
                        </label>
                        <select
                          className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          value={flow.vendor}
                          onChange={(e) => updateBiometricsFlow(idx, { vendor: e.target.value as any })}
                          disabled={disabled}
                        >
                          {(
                            (flowVendorField?.options || [
                              { value: 'unknown', label: mapLabel("Don't know") },
                              { value: 'no_vendor', label: 'No Vendor' },
                              { value: 'incode', label: 'Incode' },
                              { value: 'daon', label: 'Daon' },
                            ]) as Array<{ value: string; label: string }>
                          ).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="h-10 px-3 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] disabled:opacity-50 flex-shrink-0"
                        onClick={() => removeBiometricsFlow(idx)}
                        disabled={disabled}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mt-4">
                      <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                        Notes
                        <Tooltip content="Add any additional context about IDV requirements, vendor preferences, or special considerations.">
                          <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                        </Tooltip>
                      </label>
                      <div className="md:col-span-12">
                        <label className="block text-sm text-[var(--text-secondary)] mb-2">Notes (optional)</label>
                        <input
                          type="text"
                          className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          placeholder="Optional notes for this flow"
                          value={flow.notes}
                          onChange={(e) => updateBiometricsFlow(idx, { notes: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                      <div className="md:col-span-7">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--text-primary)]">Screens / Steps (fixed order)</h5>
                          <Tooltip content="Select which screens customers will see during verification. Screens appear in this fixed order.">
                            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                          </Tooltip>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowConsentScreenField?.formLabel || 'Consent Screen'}
                              <Tooltip content={flowConsentScreenField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-consent`}
                              value={(flow.consentScreenEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { consentScreenEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowIdFormField?.formLabel || 'ID Form'}
                              <Tooltip content={flowIdFormField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-idform`}
                              value={(flow.idFormEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { idFormEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowIdVerificationField?.formLabel || 'ID Verification'}
                              <Tooltip content={flowIdVerificationField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-idv`}
                              value={(flow.idVerificationEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { idVerificationEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          {flow.vendor === 'daon' && (
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              Double ID Flow
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-doubleid`}
                              value={(flow.doubleIdEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { doubleIdEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          )}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowBiometricsField?.formLabel || 'Biometrics'}
                              <Tooltip content={flowBiometricsField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-bio`}
                              value={(flow.biometricsEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { biometricsEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowReviewScreenField?.formLabel || 'Review Screen'}
                              <Tooltip content={flowReviewScreenField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-review`}
                              value={(flow.reviewScreenEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { reviewScreenEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              {flowRedirectPageField?.formLabel || 'Redirect Page'}
                              <Tooltip content={flowRedirectPageField?.tooltip || ''}>
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-redirect`}
                              value={(flow.redirectPageEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { redirectPageEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                              Custom Screen
                              <Tooltip content="Add a custom screen to this flow. Use the details field to describe the screen's purpose and content.">
                                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                              </Tooltip>
                            </div>
                            <TriStateToggle
                              name={`bio-flow-${idx}-custom`}
                              value={(flow.customScreenEnabled || 'unknown') as TriState}
                              onChange={(v) => updateBiometricsFlow(idx, { customScreenEnabled: v as any })}
                              disabled={disabled}
                            />
                          </div>
                          {flow.customScreenEnabled === 'yes' && (
                            <div className="ml-2 mt-1">
                              <input
                                type="text"
                                className="w-full h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                placeholder="Describe the custom screen..."
                                value={flow.customScreenDetails || ''}
                                onChange={(e) => updateBiometricsFlow(idx, { customScreenDetails: e.target.value })}
                                disabled={disabled}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="md:col-span-5">
                        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                          Capture Document Types
                          <Tooltip content="Select which document types customers can use for verification.">
                            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                          </Tooltip>
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                              checked={flow.documentTypes.includes('unknown')}
                              onChange={() => toggleFlowDocumentType(idx, 'unknown')}
                              disabled={disabled}
                            />
                            <span className="text-sm text-[var(--text-primary)]">{mapLabel("Don't know")}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                              checked={flow.documentTypes.includes('passport')}
                              onChange={() => toggleFlowDocumentType(idx, 'passport')}
                              disabled={disabled}
                            />
                            <span className="text-sm text-[var(--text-primary)]">Passport</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                              checked={flow.documentTypes.includes('drivers_license')}
                              onChange={() => toggleFlowDocumentType(idx, 'drivers_license')}
                              disabled={disabled}
                            />
                            <span className="text-sm text-[var(--text-primary)]">Driver's License</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                              checked={flow.documentTypes.includes('medicare_manual')}
                              onChange={() => toggleFlowDocumentType(idx, 'medicare_manual')}
                              disabled={disabled}
                            />
                            <span className="text-sm text-[var(--text-primary)]">Medicare card (Manual input)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                              checked={flow.documentTypes.includes('custom')}
                              onChange={() => toggleFlowDocumentType(idx, 'custom')}
                              disabled={disabled}
                            />
                            <span className="text-sm text-[var(--text-primary)]">Custom (specify)</span>
                          </label>
                          {flow.documentTypes.includes('custom') && (
                            <div className="ml-6">
                              <input
                                type="text"
                                className="w-full h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                                placeholder="Describe custom document type(s)..."
                                value={flow.customDocumentTypeDetails || ''}
                                onChange={(e) => updateBiometricsFlow(idx, { customDocumentTypeDetails: e.target.value })}
                                disabled={disabled}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {biometricsConfig.flows.length === 0 && (
                <div className="text-sm text-[var(--text-secondary)]">No flows yet. Add a flow to define the IDV journey.</div>
              )}
            </div>
          </div>
          </div>

          {/* Frontend Implementation */}
          <div className={`border border-[var(--border-color)] rounded-lg p-4 ${isFrontendIncomplete ? incompleteClass : ''}`}>
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-medium text-[var(--text-primary)]">
                {frontendSection?.formTitle || frontendGroup?.formTitle || 'Frontend Implementation'}
              </h4>
              <Tooltip
                content={
                  frontendGroup?.tooltip ||
                  frontendChoiceField?.formDescription ||
                  'How will customers access the verification experience?'
                }
              >
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            </div>
            <p className="text-xs text-[var(--text-muted)] mb-3">
              {frontendSection?.formDescription ||
                'Choose how the verification interface will be delivered to your customers. This determines the technical integration approach and level of customization available.'}
            </p>
            <div className="flex flex-wrap gap-4">
              {(
                frontendChoiceField?.options
                  ? frontendChoiceField.options.map((o) => ({ id: o.value, label: o.label }))
                  : FRONTEND_OPTIONS
              ).map((option) => (
                <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="frontend"
                    className="w-4 h-4 text-primary focus:ring-primary"
                    checked={biometricsConfig.frontend === option.id}
                    onChange={() =>
                      updateBiometricsConfig('frontend', option.id as typeof biometricsConfig.frontend)
                    }
                    disabled={disabled}
                  />
                  <span className="text-sm text-[var(--text-primary)]">{mapLabel(option.label)}</span>
                </label>
              ))}
            </div>

            {biometricsConfig.frontend === 'other' && (
              <div className="mt-4">
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  {frontendOtherField?.formLabel || 'Describe Frontend Solution'}
                </label>
                <input
                  type="text"
                  className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder={frontendOtherField?.placeholder || 'Enter frontend solution details'}
                  value={biometricsConfig.otherFrontend}
                  onChange={(e) => updateBiometricsConfig('otherFrontend', e.target.value)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {/* Custom SMS Message */}
          <div className="border border-[var(--border-color)] rounded-lg p-4">
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-medium text-[var(--text-primary)]">
                  {smsSection?.formTitle || smsGroup?.formTitle || 'Custom SMS message'}
                </h4>
                <Tooltip
                  content={
                    smsGroup?.tooltip ||
                    'Customize the SMS message sent to customers with their verification link.'
                  }
                >
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {smsSection?.formDescription ||
                  smsTemplateField?.formDescription ||
                  "Personalize the message customers receive when you send verification links via SMS. Use {{customer_name}} to automatically insert the customer's name."}
              </p>
            </div>

            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="text-sm text-[var(--text-primary)]">SMS settings confirmed</div>
              <TriStateToggle
                name="bio-sms-confirmed"
                value={(biometricsConfig.smsSettingsConfirmed || 'unknown') as TriState}
                onChange={(v) => updateBiometricsConfig('smsSettingsConfirmed', v as any)}
                disabled={disabled}
              />
            </div>
            <div className="mb-3">
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={biometricsConfig.customSmsNotRequired || false}
                  onChange={(e) => updateBiometricsConfig('customSmsNotRequired', e.target.checked)}
                  disabled={disabled}
                  className="rounded border-[var(--border-color)] text-primary focus:ring-primary"
                />
                Custom SMS text not required — use default template
              </label>
            </div>
            {!biometricsConfig.customSmsNotRequired && (
              <textarea
                className="w-full min-h-[120px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder={smsTemplateField?.placeholder || 'Enter the SMS message template...'}
                value={biometricsConfig.customSmsMessageTemplate}
                onChange={(e) => updateBiometricsConfig('customSmsMessageTemplate', e.target.value)}
                disabled={disabled}
              />
            )}

            <div className="mt-3 rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-[var(--text-secondary)] space-y-1.5">
              <p>
                <span className="font-medium text-[var(--text-primary)]">Default SMS Sender:</span>{' '}
                The mobile number customers will receive SMS from is <span className="font-mono font-medium text-[var(--text-primary)]">+61 480 017 385</span>.
                FrankieOne is an originating telco for Twilio.
              </p>
              <p>
                <span className="font-medium text-[var(--text-primary)]">Custom Sender ID:</span>{' '}
                Customers can apply for their own SMS Sender ID via the{' '}
                <a
                  href="https://www.acma.gov.au/approved-telcos-and-message-providers-sms-sender-id-register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  ACMA SMS Sender ID Register
                </a>.
                There are additional pricing implications when setting up a custom Sender ID.
              </p>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  {smsValidityHoursField?.formLabel ||
                    smsValidityGroup?.formTitle ||
                    'Onboarding link validity (hours)'}
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder={smsValidityHoursField?.placeholder || 'e.g. 24'}
                  value={biometricsConfig.smsLinkValidityHours === null ? '' : String(biometricsConfig.smsLinkValidityHours)}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw.trim() === '') {
                      updateBiometricsConfig('smsLinkValidityHours', null)
                      return
                    }
                    const n = Math.max(0, Math.floor(Number(raw)))
                    updateBiometricsConfig('smsLinkValidityHours', Number.isFinite(n) ? n : null)
                  }}
                  disabled={disabled}
                />
              </div>
              <div className="text-xs text-[var(--text-muted)] flex items-center">
                {smsValidityGroup?.helperText ||
                  smsValidityHoursField?.formDescription ||
                  'Optional: how long the SMS onboarding link remains valid.'}
              </div>
            </div>
          </div>

          {/* Branding Requirements */}
          <div className="pt-2">
            <div className="flex items-center gap-2">
              <h4 className="text-base font-semibold text-[var(--text-primary)]">
                {brandingSection?.formTitle || brandingGroup?.formTitle || 'Branding Requirements'}
              </h4>
              <Tooltip
                content={brandingGroup?.tooltip || 'Select which branding customizations you need for this client.'}
              >
                <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
              </Tooltip>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-0.5">
              {brandingSection?.formDescription ||
                'Branding options allow you to customize the verification experience to match your client\'s brand identity. Select all customizations that apply.'}
            </p>
          </div>
          <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-3">
            <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)]">
                    {rebrandingHostedOneSdkField?.formLabel || 'Rebranding HostedOneSDK'}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {rebrandingHostedOneSdkField?.formDescription || 'Custom branding on hosted solution'}
                  </p>
                </div>
                <TriStateToggle
                  name="bio-branding-hosted"
                  value={(biometricsConfig.rebrandingHostedOneSDK || 'unknown') as TriState}
                  onChange={(v) => updateBiometricsConfig('rebrandingHostedOneSDK', v as any)}
                  disabled={disabled}
                />
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)]">
                    {rebrandingDaonField?.formLabel || 'Rebranding Daon'}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {rebrandingDaonField?.formDescription || 'Custom branding on Daon integration'}
                  </p>
                </div>
                <TriStateToggle
                  name="bio-branding-daon"
                  value={(biometricsConfig.rebrandingDaon || 'unknown') as TriState}
                  onChange={(v) => updateBiometricsConfig('rebrandingDaon', v as any)}
                  disabled={disabled}
                />
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)]">
                    {extendedBrandingField?.formLabel || 'Extended Branding'}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {extendedBrandingField?.formDescription ||
                      'Custom pages, colors, logos beyond standard options'}
                  </p>
                </div>
                <TriStateToggle
                  name="bio-branding-extended"
                  value={(biometricsConfig.extendedBranding || 'unknown') as TriState}
                  onChange={(v) => updateBiometricsConfig('extendedBranding', v as any)}
                  disabled={disabled}
                />
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)]">
                    {customRedirectPageField?.formLabel || 'Custom Redirect Page'}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {customRedirectPageField?.formDescription ||
                      'Custom page after verification completion'}
                  </p>
                </div>
                <TriStateToggle
                  name="bio-branding-redirect"
                  value={(biometricsConfig.customRedirectPage || 'unknown') as TriState}
                  onChange={(v) => updateBiometricsConfig('customRedirectPage', v as any)}
                  disabled={disabled}
                />
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <div className="min-w-0">
                  <div className="text-sm text-[var(--text-primary)]">
                    {customConsentsField?.formLabel || 'Custom Consents'}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {customConsentsField?.formDescription ||
                      'Customer adding their own consent forms'}
                  </p>
                </div>
                <TriStateToggle
                  name="bio-branding-consents"
                  value={(biometricsConfig.customConsents || 'unknown') as TriState}
                  onChange={(v) => updateBiometricsConfig('customConsents', v as any)}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* Branding Notes */}
            <div>
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2">
                {brandingNotesField?.formLabel || brandingNotesGroup?.formTitle || 'Branding Notes'}
                <Tooltip
                  content={
                    brandingNotesGroup?.tooltip ||
                    'Specify brand asset locations, special requirements, or design preferences.'
                  }
                >
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </label>
              <textarea
                className="w-full h-24 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                placeholder={
                  brandingNotesField?.placeholder ||
                  'Enter any additional branding requirements or notes...'
                }
                value={biometricsConfig.brandingNotes}
                onChange={(e) => updateBiometricsConfig('brandingNotes', e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>

          {/* Daon Engagement Notes — only when Daon is involved */}
          {(biometricsConfig.vendor === 'daon' ||
            biometricsConfig.flows.some((f) => f.vendor === 'daon')) && (
            <div className="border border-amber-600/30 rounded-lg p-4 bg-amber-950/10">
              <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">Daon Engagement Notes</h4>
              <p className="text-xs text-[var(--text-muted)] mb-3">
                Internal notes about the Daon engagement. Not included in PDF or Jira exports.
              </p>
              <textarea
                className="w-full h-28 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                placeholder="Describe the Daon engagement details, timelines, contacts, special requirements..."
                value={biometricsConfig.daonEngagementNotes}
                onChange={(e) => updateBiometricsConfig('daonEngagementNotes', e.target.value)}
                disabled={disabled}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
